import { memo, useCallback, useEffect, useMemo } from "react";
import ReactFlow, { Background, BackgroundVariant, Handle, Position, type Edge, type Node, type NodeProps } from "reactflow";

import "reactflow/dist/style.css";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";

import { Clock3, ToggleRight, SlidersHorizontal, ArrowRightCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { RulesApiFactory } from "@/apis/research/rules";

const ruleStepIcons = {
  when: Clock3,
  on: ToggleRight,
  how: SlidersHorizontal,
  then: ArrowRightCircle,
};

export const CHANNEL_LABELS = {
  amazon: "Amazon",
  flipkart: "Flipkart",
  myntra: "Myntra",
  reddit: "Reddit",
  youtube: "YouTube",
  x: "X",
  instagram: "Instagram",
  tiktok: "TikTok",
} as const;

export type Channel = keyof typeof CHANNEL_LABELS;

/** Maps API `platform` slug to list/summary label. */
export function displayChannelLabel(platform: string): string {
  if (platform in CHANNEL_LABELS) return CHANNEL_LABELS[platform as Channel];
  return platform;
}

export const ALL_CHANNELS = Object.keys(CHANNEL_LABELS) as Channel[];

export const ACTION_MODE_LABELS = {
  auto_respond: "Auto-respond",
  needs_review: "Needs Review",
  flag_only: "Flag only",
} as const;

export type ActionMode = keyof typeof ACTION_MODE_LABELS;

/** Maps API `perform` (snake_case or legacy label) to a display label. */
export function displayActionModeLabel(perform: string): string {
  if (perform in ACTION_MODE_LABELS) return ACTION_MODE_LABELS[perform as ActionMode];
  return perform;
}

export const WHEN_TYPE_LABELS = {
  star_rating: "Star Rating",
  keywords: "Keywords",
  ai_sentiment: "AI Sentiment",
  engagement_spike: "Engagement Spike",
  ai_topic: "AI Topic",
} as const;

export type WhenType = keyof typeof WHEN_TYPE_LABELS;

/** Maps API `trigger` (snake_case or legacy title-case) to a summary/list label. */
export function displayRuleTriggerLabel(trigger: string): string {
  if (trigger in WHEN_TYPE_LABELS) return WHEN_TYPE_LABELS[trigger as WhenType];
  return trigger;
}

export const AI_SENTIMENT_LABELS = {
  positive: "Positive",
  negative: "Negative",
  both: "Both",
} as const;

export type AiSentiment = keyof typeof AI_SENTIMENT_LABELS;

const AI_SENTIMENT_OPTIONS = (Object.keys(AI_SENTIMENT_LABELS) as AiSentiment[]).map((value) => ({
  value,
  label: AI_SENTIMENT_LABELS[value],
}));

export type ThenActionId = "flag_moderation_queue" | "send_notification" | "assign_priority_high";

const actionModes: { id: string; value: ActionMode; title: string; subtitle: string }[] = [
  {
    id: "action-auto-respond",
    value: "auto_respond",
    title: ACTION_MODE_LABELS.auto_respond,
    subtitle: "AI handles end-to-end, no human needed",
  },
  {
    id: "action-needs-review",
    value: "needs_review",
    title: ACTION_MODE_LABELS.needs_review,
    subtitle: "AI generates draft, routes to review queue",
  },
  {
    id: "action-flag-only",
    value: "flag_only",
    title: ACTION_MODE_LABELS.flag_only,
    subtitle: "No response generated, flagged for manual handling",
  },
];

const thenActionsCatalog: { id: ThenActionId; label: string }[] = [
  { id: "flag_moderation_queue", label: "Flag for moderation queue" },
  { id: "send_notification", label: "Send notification" },
  { id: "assign_priority_high", label: "Assign priority: high" },
];

const thenActionLabelById: Record<ThenActionId, string> = {
  flag_moderation_queue: "Flag for moderation queue",
  send_notification: "Send notification",
  assign_priority_high: "Assign priority: high",
};

const thenActionClassNameById: Record<ThenActionId, string> = {
  flag_moderation_queue: "border-amber-200 bg-amber-50 text-amber-900",
  send_notification: "border-sky-200 bg-sky-50 text-sky-900",
  assign_priority_high: "border-red-200 bg-red-50 text-red-900",
};

export type RuleDraft = {
  whenType: WhenType;
  whenStarRatingBelow: number;
  whenKeywords: string;
  whenEngagementsAbove: number;
  whenAiSentiment: AiSentiment;
  aiTopics: string[];
  channels: Channel[];
  actionMode: ActionMode;
  autoRespondDowngradeToReviewBelowConfidence: number; // percent (50-95)
  thenActions: ThenActionId[];
};

export const defaultRuleDraft: RuleDraft = {
  whenType: "ai_topic",
  whenStarRatingBelow: 2,
  whenKeywords: "sizing, too small, too big, wrong size",
  whenEngagementsAbove: 5000,
  whenAiSentiment: "positive",
  aiTopics: [],
  channels: ["x"],
  actionMode: "needs_review",
  autoRespondDowngradeToReviewBelowConfidence: 68,
  thenActions: ["flag_moderation_queue", "send_notification", "assign_priority_high"],
};

type RuleBuilderFlowProps = {
  ruleDraft: RuleDraft;
  setRuleDraft: React.Dispatch<React.SetStateAction<RuleDraft>>;
};

type SectionNodeData = {
  badge: { letter: string; label: string };
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  accent: "indigo" | "amber";
};

const SectionNode = memo(function SectionNode({ data }: NodeProps<SectionNodeData>) {
  const accent =
    data.accent === "indigo"
      ? { ring: "ring-indigo-500/20", badgeBg: "bg-indigo-500/10", badgeText: "text-indigo-700", border: "border-indigo-200/60" }
      : { ring: "ring-amber-500/20", badgeBg: "bg-amber-500/10", badgeText: "text-amber-700", border: "border-amber-200/60" };

  const Icon = ruleStepIcons[data.badge.label?.toLowerCase() as keyof typeof ruleStepIcons];

  return (
    <div className={`w-[520px] rounded-2xl border bg-card shadow-sm ring-1 ${accent.ring} ${accent.border}`}>
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-muted-foreground/40 !border-0" />
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className={`size-9 rounded-lg grid place-items-center text-xs font-bold ${accent.badgeBg} ${accent.badgeText}`}>{Icon ? <Icon className="size-5" /> : data.badge.letter}</div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-foreground">{data.badge.label}</div>
              <div className="text-sm font-normal text-muted-foreground">{data.title}</div>
            </div>
          </div>
        </div>

        {/* {data.subtitle ? <div className="mt-3 text-xs text-muted-foreground">{data.subtitle}</div> : null} */}
        <div className="mt-3">{data.content}</div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-muted-foreground/40 !border-0" />
    </div>
  );
});

function Chip({ active, children, onClick }: { active?: boolean; children: React.ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center h-7 px-3 rounded-full text-xs font-medium border transition-colors",
        active ? "bg-foreground text-background border-transparent" : "bg-muted/30 text-foreground border-border hover:bg-muted/50",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

const FALLBACK_AI_TOPIC_OPTIONS: string[] = ["Defect", "Comfort", "Size", "Quality", "Pricing", "Durability", "Style", "Influencer"];

export function RuleBuilderFlow({ ruleDraft, setRuleDraft }: RuleBuilderFlowProps) {
  const allChannels = useMemo((): Channel[] => [...ALL_CHANNELS], []);
  const nodeTypes = useMemo(() => ({ section: SectionNode }), []);

  const { data: topicSuggestionsResponse } = useQuery(RulesApiFactory.Queries.AiTopicSuggestions());

  useEffect(() => {
    const topics = topicSuggestionsResponse?.data;
    if (!Array.isArray(topics) || topics.length === 0) return;
    const first = topics[0];
    if (typeof first !== "string" || !first.trim()) return;
    setRuleDraft((prev) => {
      if (prev.aiTopics.length > 0) return prev;
      return { ...prev, aiTopics: [first] };
    });
  }, [topicSuggestionsResponse, setRuleDraft]);

  const aiTopics = useMemo((): string[] => {
    const raw = topicSuggestionsResponse?.data;
    if (!Array.isArray(raw)) return FALLBACK_AI_TOPIC_OPTIONS;

    return raw.length > 0 ? raw : FALLBACK_AI_TOPIC_OPTIONS;
  }, [topicSuggestionsResponse]);

  const setWhenType = useCallback(
    (whenType: WhenType) => {
      setRuleDraft((prev) => ({ ...prev, whenType }));
    },
    [setRuleDraft]
  );

  const setWhenStarRatingBelow = useCallback(
    (whenStarRatingBelow: RuleDraft["whenStarRatingBelow"]) => {
      setRuleDraft((prev) => ({ ...prev, whenStarRatingBelow }));
    },
    [setRuleDraft]
  );

  const setWhenKeywords = useCallback(
    (whenKeywords: string) => {
      setRuleDraft((prev) => ({ ...prev, whenKeywords }));
    },
    [setRuleDraft]
  );

  const setWhenEngagementsAbove = useCallback(
    (whenEngagementsAbove: number) => {
      setRuleDraft((prev) => ({ ...prev, whenEngagementsAbove }));
    },
    [setRuleDraft]
  );

  const setWhenAiSentiment = useCallback(
    (whenAiSentiment: AiSentiment) => {
      setRuleDraft((prev) => ({ ...prev, whenAiSentiment }));
    },
    [setRuleDraft]
  );

  const toggleAiTopic = useCallback(
    (topic: string) => {
      setRuleDraft((prev) => {
        const exists = prev.aiTopics.includes(topic);
        return { ...prev, aiTopics: exists ? prev.aiTopics.filter((t) => t !== topic) : [...prev.aiTopics, topic] };
      });
    },
    [setRuleDraft]
  );

  const toggleChannel = useCallback(
    (channel: Channel) => {
      setRuleDraft((prev) => {
        const exists = prev.channels.includes(channel);
        return { ...prev, channels: exists ? prev.channels.filter((c) => c !== channel) : [...prev.channels, channel] };
      });
    },
    [setRuleDraft]
  );

  const setActionMode = useCallback(
    (actionMode: ActionMode) => {
      setRuleDraft((prev) => ({ ...prev, actionMode }));
    },
    [setRuleDraft]
  );

  const setAutoRespondDowngradeToReviewBelowConfidence = useCallback(
    (autoRespondDowngradeToReviewBelowConfidence: number) => {
      setRuleDraft((prev) => ({ ...prev, autoRespondDowngradeToReviewBelowConfidence }));
    },
    [setRuleDraft]
  );

  const addThenAction = useCallback(
    (actionId: ThenActionId) => {
      setRuleDraft((prev) => {
        if (prev.thenActions.includes(actionId)) return prev;
        return { ...prev, thenActions: [...prev.thenActions, actionId] };
      });
    },
    [setRuleDraft]
  );

  const removeThenAction = useCallback(
    (actionId: ThenActionId) => {
      setRuleDraft((prev) => ({ ...prev, thenActions: prev.thenActions.filter((a) => a !== actionId) }));
    },
    [setRuleDraft]
  );

  const availableThenActions = useMemo(() => thenActionsCatalog.filter((a) => !ruleDraft.thenActions.includes(a.id)), [ruleDraft.thenActions]);

  const nodes = useMemo<Node<SectionNodeData>[]>(
    () => [
      {
        id: "when",
        type: "section",
        position: { x: 110, y: 60 },
        data: {
          accent: "indigo",
          badge: { letter: "W", label: "WHEN" },
          title: "Trigger condition",
          subtitle:
            ruleDraft.whenType === "ai_topic"
              ? ruleDraft.aiTopics.length === 0
                ? "Pick AI topics"
                : ruleDraft.aiTopics.length === aiTopics.length
                ? "All AI topics"
                : `${ruleDraft.aiTopics.length} selected`
              : WHEN_TYPE_LABELS[ruleDraft.whenType],
          content: (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <Select value={ruleDraft.whenType} onValueChange={(v) => setWhenType(v as WhenType)}>
                  <SelectTrigger className="w-full bg-muted/30">
                    <SelectValue placeholder="Select a trigger type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="star_rating">{WHEN_TYPE_LABELS.star_rating}</SelectItem>
                    <SelectItem value="keywords">{WHEN_TYPE_LABELS.keywords}</SelectItem>
                    <SelectItem value="ai_sentiment">{WHEN_TYPE_LABELS.ai_sentiment}</SelectItem>
                    {/* <SelectItem value="engagement_spike">{WHEN_TYPE_LABELS.engagement_spike}</SelectItem> */}
                    <SelectItem value="ai_topic">{WHEN_TYPE_LABELS.ai_topic}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {ruleDraft.whenType === "star_rating" ? (
                <div className="rounded-2xl flex flex-wrap items-center gap-4">
                  <div className="text-sm text-muted-foreground">Rating below</div>
                  <Select value={String(ruleDraft.whenStarRatingBelow)} onValueChange={(v) => setWhenStarRatingBelow(Number(v) as RuleDraft["whenStarRatingBelow"])}>
                    <SelectTrigger className="w-[120px] bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n} ★
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-sm text-muted-foreground">stars</div>
                </div>
              ) : null}

              {ruleDraft.whenType === "keywords" ? (
                <div className="rounded-2xl space-y-2">
                  <Textarea value={ruleDraft.whenKeywords} onChange={(e) => setWhenKeywords(e.target.value)} className="bg-background" />
                  <div className="text-xs text-muted-foreground">Separate keywords with commas</div>
                </div>
              ) : null}

              {ruleDraft.whenType === "ai_sentiment" ? (
                <div className="rounded-2xl">
                  <RadioGroup value={ruleDraft.whenAiSentiment} onValueChange={(v) => setWhenAiSentiment(v as AiSentiment)} className="flex items-center gap-2">
                    {AI_SENTIMENT_OPTIONS.map(({ value, label }) => (
                      <div
                        key={value}
                        className={[
                          "rounded-full border px-4 py-2 transition-colors",
                          ruleDraft.whenAiSentiment === value ? "bg-foreground text-background border-transparent" : "bg-background text-foreground border-border hover:bg-muted/30",
                        ].join(" ")}
                      >
                        <RadioGroupItem id={`sentiment-${value}`} value={value} className="sr-only" />
                        <label htmlFor={`sentiment-${value}`} className="cursor-pointer text-sm font-medium">
                          {label}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ) : null}

              {ruleDraft.whenType === "ai_topic" ? (
                <div className="flex flex-wrap gap-2">
                  {aiTopics.map((t) => (
                    <Chip key={t} active={ruleDraft.aiTopics.includes(t)} onClick={() => toggleAiTopic(t)}>
                      {t}
                    </Chip>
                  ))}
                </div>
              ) : null}

              {ruleDraft.whenType === "engagement_spike" ? (
                <div className="rounded-2xl flex flex-wrap items-center gap-3">
                  <div className="text-sm text-muted-foreground">Engagements above</div>
                  <Input
                    type="number"
                    value={String(ruleDraft.whenEngagementsAbove)}
                    onChange={(e) => setWhenEngagementsAbove(Number(e.target.value))}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="nodrag nopan bg-background w-[140px]"
                  />
                  {/* <div className="flex items-center gap-2">
                    {[1000, 5000, 10000, 50000].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setWhenEngagementsAbove(n)}
                        className={[
                          "h-8 px-3 rounded-full border text-xs font-semibold transition-colors",
                          ruleDraft.whenEngagementsAbove === n ? "bg-foreground text-background border-transparent" : "bg-background text-muted-foreground border-border hover:bg-muted/30",
                        ].join(" ")}
                      >
                        {n >= 1000 ? `${n / 1000}K` : n}
                      </button>
                    ))}
                  </div> */}
                </div>
              ) : null}
            </div>
          ),
        },
      },
      {
        id: "on",
        type: "section",
        position: { x: 110, y: 360 },
        data: {
          accent: "amber",
          badge: { letter: "O", label: "ON" },
          title: "Channels",
          subtitle: ruleDraft.channels.length === 0 ? "Pick channels" : ruleDraft.channels.length === allChannels.length ? "All channels" : `${ruleDraft.channels.length} selected`,
          content: (
            <div className="flex flex-wrap gap-2">
              {allChannels.map((c) => (
                <Chip key={c} active={ruleDraft.channels.includes(c)} onClick={() => toggleChannel(c)}>
                  {CHANNEL_LABELS[c]}
                </Chip>
              ))}
            </div>
          ),
        },
      },
      {
        id: "how",
        type: "section",
        position: { x: 110, y: 580 },
        data: {
          accent: "amber",
          badge: { letter: "H", label: "HOW" },
          title: "Action mode",
          subtitle: "Pick what happens next",
          content: (
            <div className="space-y-4">
              <RadioGroup value={ruleDraft.actionMode} onValueChange={(value) => setActionMode(value as ActionMode)} className="flex flex-col gap-y-3">
                {actionModes.map((item) => (
                  <div
                    key={item.id}
                    className={[
                      "rounded-xl outline p-3 flex items-start gap-3 transition-colors",
                      ruleDraft.actionMode === item.value ? "outline-amber-300 bg-amber-50/40" : "outline-none bg-background hover:bg-muted/30",
                    ].join(" ")}
                  >
                    <RadioGroupItem id={item.id} value={item.value} className="mt-0.5" />
                    <label htmlFor={item.id} className="cursor-pointer flex flex-col gap-0.5">
                      <div className="text-sm font-medium text-foreground">{item.title}</div>
                      <div className="text-xs text-muted-foreground">{item.subtitle}</div>
                    </label>
                  </div>
                ))}
              </RadioGroup>

              {ruleDraft.actionMode === "auto_respond" ? (
                <div className="rounded-xl border bg-background p-3">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="text-sm font-medium text-foreground">Downgrade to review if confidence below</div>
                    <div className="text-sm font-semibold tabular-nums text-foreground">{ruleDraft.autoRespondDowngradeToReviewBelowConfidence}%</div>
                  </div>

                  <div className="nodrag nopan">
                    <Slider
                      min={50}
                      max={100}
                      step={1}
                      value={[ruleDraft.autoRespondDowngradeToReviewBelowConfidence]}
                      onValueChange={(v) => setAutoRespondDowngradeToReviewBelowConfidence(v[0] ?? 68)}
                      className="[&_[data-slot=slider-range]]:bg-emerald-500 [&_[data-slot=slider-thumb]]:border-emerald-500"
                    />
                  </div>

                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground tabular-nums">
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
              ) : null}
            </div>
          ),
        },
      },
      {
        id: "then",
        type: "section",
        position: { x: 110, y: 1000 },
        data: {
          accent: "indigo",
          badge: { letter: "T", label: "THEN" },
          title: "Actions",
          subtitle: "What happens next",
          content: (
            <div className="space-y-2">
              {ruleDraft.thenActions.map((actionId) => (
                <div key={actionId} className={["rounded-lg border px-3 py-2 text-sm font-medium flex items-center justify-between", thenActionClassNameById[actionId]].join(" ")}>
                  <span>{thenActionLabelById[actionId]}</span>
                  <Button
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeThenAction(actionId);
                    }}
                    className="p-2 h-7 rounded-md bg-red-100 hover:bg-red-200 text-red-500"
                  >
                    <X className="size-3" />
                  </Button>
                </div>
              ))}

              <Popover>
                <PopoverTrigger asChild className="mt-4">
                  <button
                    type="button"
                    className="nodrag nopan w-full rounded-xl border border-dashed border-border bg-background px-3 py-3 text-sm font-medium hover:text-foreground hover:bg-muted/30 transition-colors"
                  >
                    + Add action
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="start" sideOffset={8} className=" p-2 rounded-xl">
                  <div className="px-2 py-2 text-xs font-semibold text-muted-foreground">Available actions</div>
                  <div className="space-y-1">
                    {availableThenActions.length === 0 ? (
                      <div className="px-2 py-2 text-sm text-muted-foreground">All actions already added</div>
                    ) : (
                      availableThenActions.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => {
                            addThenAction(a.id);
                          }}
                          className="w-full text-left rounded-lg px-2 py-2 text-sm font-medium hover:bg-muted/40 transition-colors"
                        >
                          {a.label}
                        </button>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          ),
        },
      },
    ],
    [
      allChannels,
      aiTopics,
      ruleDraft.actionMode,
      ruleDraft.aiTopics,
      ruleDraft.channels,
      ruleDraft.whenAiSentiment,
      ruleDraft.whenEngagementsAbove,
      ruleDraft.whenKeywords,
      ruleDraft.whenStarRatingBelow,
      ruleDraft.autoRespondDowngradeToReviewBelowConfidence,
      ruleDraft.thenActions,
      ruleDraft.whenType,
      setActionMode,
      setWhenAiSentiment,
      setWhenEngagementsAbove,
      setWhenKeywords,
      setWhenStarRatingBelow,
      setWhenType,
      toggleAiTopic,
      toggleChannel,
      availableThenActions,
      addThenAction,
      removeThenAction,
      setAutoRespondDowngradeToReviewBelowConfidence,
    ]
  );

  const edges = useMemo<Edge[]>(
    () => [
      { id: "e-when-on", source: "when", target: "on", type: "smoothstep", animated: false },
      { id: "e-on-how", source: "on", target: "how", type: "smoothstep", animated: false },
      { id: "e-how-then", source: "how", target: "then", type: "smoothstep", animated: false },
    ],
    []
  );

  return (
    <div className="h-[720px] w-full overflow-y-auto overflow-x-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.18, minZoom: 0.55, maxZoom: 1.2 }}
        panOnDrag={false}
        panOnScroll={true}
        // panOnScrollMode={PanOnScrollMode.Vertical}
        preventScrolling={false}
        zoomOnScroll={false}
        zoomOnPinch={true}
        zoomOnDoubleClick={false}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={18} size={1} variant={BackgroundVariant.Dots} />
      </ReactFlow>
    </div>
  );
}
